import * as THREE from 'three';
import { LEGACY_LIGHT_SCALE, configureLegacyRenderer } from './legacyLook';
import { applyViewAngles, sourceToThree } from './coords';
import type { LevelView } from './levelView';
import { Decals } from './decals';
import { Viewmodel } from './viewmodel';
import { EYE_DUCK, EYE_STAND } from '../sim/constants';
import { horizontalSpeed } from '../sim/player';
import type { Simulation } from '../sim/simulation';

const SKY = 0x121a23;

// Every material draws in one pass. three.js draws a transparent double-sided material twice (back
// faces, then front), flipping its side and marking it for an update each time: every frame it
// rebuilds the material's program parameters and cache key, kilobytes of garbage per material that
// the garbage collector then clears in a stall (the lag spikes). Our see-through things (zones, the
// replay bot, glass, editor previews) don't need the back faces drawn first.
Object.defineProperty(THREE.Material.prototype, 'forceSinglePass', {
  get: () => true,
  set: () => {},
  configurable: true,
});

export interface ViewAngles {
  yaw: number;
  pitch: number;
}

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly canvas: HTMLCanvasElement;
  readonly viewmodel: Viewmodel;
  readonly decals = new Decals();
  showKnife = true;
  /** Both weapons dropped: no viewmodel (arms) at all. */
  emptyHands = false;
  readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(74, 1, 4, 20000);
  private level: LevelView | null = null;
  private readonly fog = new THREE.Fog(SKY, 1800, 7000);
  readonly anisotropy: number;
  /** DXT textures can be uploaded compressed. */
  readonly supportsS3TC: boolean;
  private fov = 90;
  private maxPixelRatio = 2;
  /** Eye height above the origin, eased toward the standing/ducked height. */
  private eye = EYE_STAND;

  constructor(
    private readonly container: HTMLElement,
    lowLatency = false,
  ) {
    const options: THREE.WebGLRendererParameters = { antialias: true, powerPreference: 'high-performance' };
    if (lowLatency) {
      // A desynchronized context draws straight to the screen (see Settings.lowLatency).
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('webgl2', {
        antialias: true,
        powerPreference: 'high-performance',
        desynchronized: true,
        alpha: false,
        depth: true,
        stencil: false,
      });
      if (context) Object.assign(options, { canvas, context });
    }
    this.renderer = new THREE.WebGLRenderer(options);
    configureLegacyRenderer(this.renderer);
    this.renderer.autoClear = false;
    // Two passes per frame; count both in the debug overlay.
    this.renderer.info.autoReset = false;
    this.canvas = this.renderer.domElement;
    container.appendChild(this.canvas);

    this.scene.background = new THREE.Color(SKY);
    this.scene.fog = this.fog;
    this.camera.rotation.order = 'YXZ';
    this.scene.add(new THREE.HemisphereLight(0xc6d8ff, 0x2a2018, 0.85 * LEGACY_LIGHT_SCALE));
    const sun = new THREE.DirectionalLight(0xfff0dc, 0.75 * LEGACY_LIGHT_SCALE);
    sun.position.set(0.45, 1, 0.3);
    this.scene.add(sun);
    this.scene.add(this.decals.group);

    this.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this.supportsS3TC = this.renderer.extensions.has('WEBGL_compressed_texture_s3tc');
    this.viewmodel = new Viewmodel(this.anisotropy);

    window.addEventListener('resize', () => this.resize());
    this.resize();
  }

  /** Show a level, disposing the previous one. */
  /** Where the camera is (three.js units), as of the last frame. */
  get cameraPosition(): THREE.Vector3 {
    return this.camera.position;
  }

  setLevel(view: LevelView) {
    if (this.level) this.level.dispose();
    this.decals.clear();
    this.level = view;
    this.scene.add(view.root);
    this.fog.color.set(view.fogColor ?? SKY);
    (this.scene.background as THREE.Color).set(view.fogColor ?? SKY);
    this.fog.near = view.fog[0];
    this.fog.far = view.fog[1];
    this.camera.far = view.far;
    this.camera.updateProjectionMatrix();
    // New fog and lights: everything compiles again for this level.
    this.warmedIn.delete(this.scene);
    this.warmUp();
  }

  /**
   * Compile every shader and upload every texture now, while the loading screen is up. Otherwise
   * three.js does it the first time something comes into view, which can stall a frame for hundreds
   * of milliseconds (shader compiles are slow on Windows, where WebGL runs on Direct3D).
   */
  /**
   * Upload and compile what's new (call after adding things to the scene: weapon models, the replay
   * bot's tag, textures that arrived). This happens while the player plays, so new shaders compile
   * in parallel (compileAsync) and are only drawn once linked: a compile on the spot stalls a frame
   * for tens of milliseconds.
   */
  prewarm(): Promise<void> {
    if (this.warming) {
      this.warmAgain = true;
      return this.warming;
    }
    return (this.warming = (async () => {
      do {
        this.warmAgain = false;
        this.uploadTextures();
        for (const [scene, camera] of this.warmPairs()) {
          const fresh = this.reveal(scene);
          if (!fresh) continue;
          // compileAsync compiles now (what's revealed) and resolves once the programs are linked.
          let linked: Promise<unknown>;
          try {
            linked = this.renderer.compileAsync(scene, camera);
          } finally {
            fresh.restore();
          }
          await linked;
        }
        this.warmUp();
      } while (this.warmAgain);
    })().finally(() => (this.warming = null)));
  }

  private warming: Promise<void> | null = null;
  private warmAgain = false;

  private warmPairs(): [THREE.Scene, THREE.Camera][] {
    return [
      [this.scene, this.camera],
      [this.viewmodel.scene, this.viewmodel.camera],
    ];
  }

  /** Textures, geometries and materials (by version) already on the GPU and compiled. */
  private readonly warmed = new WeakMap<object, number>();
  /** Per scene: geometries and materials drawn there (programs depend on the scene's lights and fog). */
  private readonly warmedIn = new Map<THREE.Scene, WeakMap<object, number>>();

  /**
   * Upload and compile what isn't yet. Only new or changed things are drawn, so calling this again
   * (a texture arrived, weapon models loaded) costs little.
   */
  private warmUp() {
    this.uploadTextures();
    // compile() skips hidden objects, and geometry only reaches the GPU the first time a mesh is
    // drawn: both used to happen mid-run (a hitch when scenery came into view, on the first shot,
    // the first switch to the pistol...). Draw what's new once now, hidden, into a 1x1 target.
    for (const [scene, camera] of this.warmPairs()) this.warmDraw(scene, camera);
  }

  private uploadTextures() {
    const r = this.renderer;
    for (const scene of [this.scene, this.viewmodel.scene])
      scene.traverse((o) => {
        const mat = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (!mat) return;
        for (const m of Array.isArray(mat) ? mat : [mat]) {
          const up = (t: unknown) => {
            if (!(t instanceof THREE.Texture) || this.warmed.get(t) === t.version) return;
            r.initTexture(t);
            this.warmed.set(t, t.version);
          };
          for (const v of Object.values(m)) up(v);
          const u = (m as THREE.ShaderMaterial).uniforms;
          if (u) for (const k in u) up(u[k].value);
        }
      });
  }

  private warmTarget: THREE.WebGLRenderTarget | null = null;

  /**
   * Show only what isn't warmed yet in `scene` (everything else hidden, nothing culled). Null when
   * all of it is; otherwise `restore()` puts visibility back and `done()` marks it warmed.
   */
  private reveal(scene: THREE.Scene): { restore: () => void; done: () => void } | null {
    const saved: [THREE.Object3D, boolean, boolean][] = [];
    const fresh: object[] = [];
    let any = false;
    let warmed = this.warmedIn.get(scene);
    if (!warmed) this.warmedIn.set(scene, (warmed = new WeakMap()));
    const w = warmed;
    scene.traverse((o) => {
      saved.push([o, o.visible, o.frustumCulled]);
      o.frustumCulled = false;
      const geo = (o as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
      const mat = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (!geo || !mat) {
        o.visible = true; // groups: their children decide
        return;
      }
      let need = !w.has(geo);
      for (const m of Array.isArray(mat) ? mat : [mat])
        if (w.get(m) !== m.version) {
          need = true;
          fresh.push(m);
        }
      o.visible = need;
      if (need) {
        any = true;
        fresh.push(geo);
      }
    });
    const restore = () => {
      for (const [o, v, f] of saved) {
        o.visible = v;
        o.frustumCulled = f;
      }
    };
    if (!any) {
      restore();
      return null;
    }
    return {
      restore,
      done: () => {
        for (const x of fresh) w.set(x, (x as THREE.Material).version ?? 1);
      },
    };
  }

  private warmDraw(scene: THREE.Scene, camera: THREE.Camera) {
    const fresh = this.reveal(scene);
    if (!fresh) return;
    const r = this.renderer;
    this.warmTarget ??= new THREE.WebGLRenderTarget(1, 1);
    const prev = r.getRenderTarget();
    r.setRenderTarget(this.warmTarget);
    try {
      r.compile(scene, camera);
      r.render(scene, camera);
      fresh.done();
    } finally {
      r.setRenderTarget(prev);
      r.info.reset();
      fresh.restore();
    }
  }

  private readonly tmpV = new THREE.Vector3();
  private readonly tmpRay = new THREE.Raycaster();
  private readonly tmpNdc = new THREE.Vector2();

  /** Screen position (CSS px from the canvas' top-left) of a Source point, or null if behind. */
  projectSource(p: readonly number[]): [number, number] | null {
    const v = sourceToThree(this.tmpV, p[0], p[1], p[2]);
    this.camera.updateMatrixWorld();
    v.project(this.camera);
    if (v.z > 1 || v.z < -1) return null;
    const c = this.renderer.domElement;
    return [((v.x + 1) / 2) * c.clientWidth, ((1 - v.y) / 2) * c.clientHeight];
  }

  /** The view ray through a screen point (-1..1 each way), as a Source-space origin and direction. */
  sourceRay(ndcX: number, ndcY: number, outO: number[], outD: number[]) {
    this.camera.updateMatrixWorld();
    this.tmpRay.setFromCamera(this.tmpNdc.set(ndcX, ndcY), this.camera);
    const o = this.tmpRay.ray.origin;
    const d = this.tmpRay.ray.direction;
    // three (x, y, z) = Source (-Y, Z, -X)
    outO[0] = -o.z;
    outO[1] = -o.x;
    outO[2] = o.y;
    outD[0] = -d.z;
    outD[1] = -d.x;
    outD[2] = d.y;
  }

  setFov(fov: number) {
    this.fov = fov;
    this.updateFov();
  }

  setMaxPixelRatio(r: number) {
    this.maxPixelRatio = r;
    this.resize();
  }

  /** CS fov is horizontal at 4:3; wider screens get Hor+. */
  private updateFov() {
    const h = (this.fov * Math.PI) / 180;
    this.camera.fov = (2 * Math.atan(Math.tan(h / 2) * 0.75) * 180) / Math.PI;
    this.camera.updateProjectionMatrix();
  }

  resize() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.maxPixelRatio));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.updateFov();
    this.viewmodel.setAspect(w / h);
  }

  /**
   * Draw one frame. `alpha` is how far we are between the last two ticks (0..1).
   * `dt` is the frame time in seconds (for viewmodel animation and eye easing).
   */
  render(sim: Simulation, view: ViewAngles, alpha: number, dt: number) {
    const p = sim.player;
    const fx = p.fx;
    if (fx.eyeSnap >= 0) {
      this.eye = fx.eyeSnap;
      fx.eyeSnap = -1;
    }
    this.eye += fx.eyeShift;
    fx.eyeShift = 0;
    const target = p.ducked ? EYE_DUCK : EYE_STAND;
    this.eye += (target - this.eye) * Math.min(1, dt * 14);

    const x = p.prev[0] + (p.pos[0] - p.prev[0]) * alpha;
    const y = p.prev[1] + (p.pos[1] - p.prev[1]) * alpha;
    const z = p.prev[2] + (p.pos[2] - p.prev[2]) * alpha;
    sourceToThree(this.camera.position, x, y, z + this.eye);
    applyViewAngles(this.camera, view.yaw, view.pitch);

    this.level?.update(sim, alpha);

    if (this.showKnife && !this.emptyHands) {
      this.viewmodel.update(dt, {
        speed: horizontalSpeed(p),
        onGround: p.ground !== null,
        vz: p.vel[2],
        landImpact: fx.landImpact,
      });
      fx.landImpact = 0;
    }

    const r = this.renderer;
    r.info.reset();
    r.clear();
    r.render(this.scene, this.camera);
    if (this.showKnife && !this.emptyHands) {
      r.clearDepth();
      r.render(this.viewmodel.scene, this.viewmodel.camera);
    }
    if (this.inset) this.renderInset(this.inset);
  }

  /**
   * Level builder side view: a small flat (orthographic) picture from the side, looking the way the
   * camera faces, centred on a point (Source space). Shows heights at a glance. null hides it.
   */
  inset: { center: readonly number[]; yaw: number; width: number } | null = null;
  /** Where the side view sits on screen (CSS px from the bottom-left). */
  readonly insetRect = { x: 12, y: 12, w: 340, h: 200 };
  private readonly sideCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 40000);

  private renderInset(v: { center: readonly number[]; yaw: number; width: number }) {
    const r = this.renderer;
    const { x, y, w, h } = this.insetRect;
    const cam = this.sideCam;
    const halfW = v.width / 2;
    const halfH = (halfW * h) / w;
    cam.left = -halfW;
    cam.right = halfW;
    cam.top = halfH;
    cam.bottom = -halfH;
    cam.updateProjectionMatrix();
    // Look horizontally along the view's direction, from far behind the point.
    const back = 20000;
    sourceToThree(
      cam.position,
      v.center[0] - Math.cos(v.yaw) * back,
      v.center[1] - Math.sin(v.yaw) * back,
      v.center[2],
    );
    sourceToThree(this.tmpV, v.center[0], v.center[1], v.center[2]);
    cam.lookAt(this.tmpV);
    const fog = this.scene.fog;
    const bg = this.scene.background;
    this.scene.fog = null;
    this.scene.background = INSET_BG;
    r.setScissorTest(true);
    r.setScissor(x, y, w, h);
    r.setViewport(x, y, w, h);
    r.clear();
    r.render(this.scene, cam);
    r.setScissorTest(false);
    const size = r.getSize(this.tmpSize);
    r.setViewport(0, 0, size.x, size.y);
    this.scene.fog = fog;
    this.scene.background = bg;
  }

  private readonly tmpSize = new THREE.Vector2();
}

const INSET_BG = new THREE.Color(0x101820);
